import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TvForm as Form,
  TvLabel as Label,
  TvInput,
  TvRow as Row,
  TvButton,
  TvDiv,
  TvP,
} from "@trustvid/design-system";
import { actorSendOTP, actorVerifyOTP } from "../../../services/auth";
import { toast } from "react-toastify";
import FullScreenLoader from "../../../components/CommonForBoth/loader";
import { jwtDecode } from "jwt-decode";
import { persistActorData } from "../../../utils/auth-helpers";
import useActorInviteBranding from "../../../hooks/useActorInviteBranding";
import AuthSplitLayout from "./AuthSplitLayout";
import { ACTOR_VERIFY_PAGE_COPY, AUTH_FIELD_LABEL_CLASS } from "./loginPageContent";

const ActorVerify = () => {
  const [timer, setTimer] = useState(60);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const mobileNo = localStorage.getItem("actorMobileNo");
  const { token } = useParams();
  const [loading, setloading] = useState(false);
  useActorInviteBranding(token, { applyTheme: false });

  useEffect(() => {
    let interval = null;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setResendEnabled(true);
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [timer]);

  const applyOtpDigits = (rawValue, startIndex = 0) => {
    const digits = String(rawValue).replace(/\D/g, "").slice(0, 6 - startIndex);
    if (!digits) {
      return;
    }

    const newOtp = [...otp];
    digits.split("").forEach((digit, offset) => {
      const targetIndex = startIndex + offset;
      if (targetIndex < newOtp.length) {
        newOtp[targetIndex] = digit;
      }
    });
    setOtp(newOtp);

    const nextFocusIndex = Math.min(startIndex + digits.length, newOtp.length - 1);
    document.getElementById(`digit${nextFocusIndex + 1}-input`)?.focus();
  };

  const handleChange = (index, value) => {
    const digitsOnly = String(value).replace(/\D/g, "");
    if (!digitsOnly) {
      const cleared = [...otp];
      cleared[index] = "";
      setOtp(cleared);
      return;
    }

    if (digitsOnly.length > 1) {
      applyOtpDigits(digitsOnly, index);
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = digitsOnly;
    setOtp(newOtp);

    if (index < otp.length - 1) {
      document.getElementById(`digit${index + 2}-input`)?.focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    applyOtpDigits(e.clipboardData.getData("text"), index);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];

      if (otp[index] === "" && index > 0) {
        document.getElementById(`digit${index}-input`).focus();
      }

      newOtp[index] = "";
      setOtp(newOtp);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setloading(true);
    const mobile_no = localStorage.getItem("actorMobileNo");
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) {
      setloading(false);
      setError("Please enter all 6 digits.");
      return;
    }
    setError(null);
    try {
      const response = await actorVerifyOTP({ mobile_no, otp: enteredOtp, token: token });
      if (response && !response.error) {
        setloading(false);
        localStorage.removeItem("actorMobileNo");
        localStorage.setItem("actorToken", response?.data?.token);
        localStorage.setItem("userType", "actor");
        persistActorData(response?.data);

        const actorToken = localStorage.getItem("actorToken");
        let campaignId;
        let actorId;

        if (actorToken) {
          try {
            const decoded = jwtDecode(actorToken);
            campaignId = decoded?.campaign_id;
            actorId = decoded?.actor_id;
          } catch (decodeError) {
            console.error("Invalid actor token", decodeError);
          }
        }

        if (response?.data?.user?.consent) {
          navigate(`/campaign-table/${campaignId}/videos/${actorId}`);
        } else {
          navigate("/actor-form");
        }
      } else {
        setloading(false);
        setError(response.error || "Failed to verify OTP.");
      }
    } catch (err) {
      setloading(false);
      setError("An error occurred. Please try again.");
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    setloading(true);
    try {
      setOtp(["", "", "", "", "", ""]);
      setTimer(60);
      setResendEnabled(false);
      const response = await actorSendOTP({ mobile_no: mobileNo, token: token });
      if (response && !response.error) {
        setloading(false);
        toast.success("OTP sent successfully.");
      } else {
        setloading(false);
      }
    } catch (err) {
      setloading(false);
      setError("An error occurred. Please try again.");
    }
  };

  document.title = "Two Step Verification";

  const formattedTimer = String(timer).padStart(2, "0");

  return (
    <>
      <FullScreenLoader loading={loading} setLoading={setloading} />
      <AuthSplitLayout
        pageTestId="actor-verify-page"
        cardTestId="actor-verify-form-card"
        cardTitle={ACTOR_VERIFY_PAGE_COPY.pageTitle}
        welcomeHeading={ACTOR_VERIFY_PAGE_COPY.pageTitle}
        welcomeHeadingTestId="actor-verify-card-title"
        cardSubtitle={ACTOR_VERIFY_PAGE_COPY.cardSubtitle(mobileNo)}
        featurePanelHeading={ACTOR_VERIFY_PAGE_COPY.featurePanelHeading}
        showCardHeading={false}
      >
        <Form onSubmit={handleSubmit} data-testid="actor-verify-form" className="tv-login-page__form">
            <Label htmlFor="digit1-input" className={`${AUTH_FIELD_LABEL_CLASS} d-block text-dark`}>
              {ACTOR_VERIFY_PAGE_COPY.otpLabel}
            </Label>
            <Row>
              {otp.map((digit, index) => (
                <TvDiv key={index} className="col-2 px-sm-2 px-1">
                  <TvDiv className="mb-3">
                    <Label htmlFor={`digit${index + 1}-input`} className="visually-hidden">
                      Digit {index + 1}
                    </Label>
                    <TvInput
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      className="form-control form-control-lg text-center tv-login-page__field p-0 m-0"
                      maxLength={1}
                      id={`digit${index + 1}-input`}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={(e) => handlePaste(e, index)}
                    />
                  </TvDiv>
                </TvDiv>
              ))}
            </Row>
            {error ? <TvP className="text-danger">{error}</TvP> : null}

            <TvDiv className="my-0 d-flex align-items-center justify-content-between">
              <TvP className="mb-0 text-muted">
                {!resendEnabled && `Resend in ${Math.floor(formattedTimer / 60)} : ${formattedTimer}`}
              </TvP>
              <TvButton
                type="button"
                color="link"
                onClick={handleResend}
                disabled={!resendEnabled}
                className={`fw-medium text-primary p-0 ${resendEnabled ? "" : "disabled"}`}
              >
                Resend
              </TvButton>
            </TvDiv>

            <TvDiv className="d-grid mt-4">
              <TvButton type="submit" color="primary" className="tv-login-page__submit">
                Confirm
              </TvButton>
            </TvDiv>
        </Form>
      </AuthSplitLayout>
    </>
  );
};

export default ActorVerify;
